import {
  optionalMutationBody,
  optionalQueryParams,
} from "../apiEndpointHelpers";
import type { ApiEndpointBuilder } from "../apiEndpointHelpers";

import type {
  AdminV2GrowthSequenceDetailResponse,
  AdminV2GrowthSequenceEnrollPersonResponse,
  AdminV2GrowthSequencePersonEnrollmentsResponse,
  AdminV2GrowthSequencesListResponse,
  AdminV2GrowthSequenceStatus,
  AdminV2GrowthSequenceUnenrollResponse,
  JsonValue as AdminV2JsonValue,
} from "@/types/adminV2";

export const buildAdminV2GrowthSequencesEndpoints = (
  builder: ApiEndpointBuilder,
) => ({
  getAdminV2GrowthSequences: builder.query<
    AdminV2GrowthSequencesListResponse,
    {
      organisationId?: string;
      q?: string;
      status?: AdminV2GrowthSequenceStatus;
      limit?: number;
      offset?: number;
    } | void
  >({
    query: (params) => ({
      url: "/admin/v2/growth/sequences",
      params: optionalQueryParams(params),
    }),
    providesTags: [{ type: "AdminV2GrowthSequences", id: "LIST" }],
    keepUnusedDataFor: 20,
  }),

  createAdminV2GrowthSequence: builder.mutation<
    AdminV2GrowthSequenceDetailResponse,
    {
      organisationId: string;
      sequenceKey?: string;
      name: string;
      description?: string | null;
      builderSchema: AdminV2JsonValue;
      stopConditions?: AdminV2JsonValue;
      metadata?: AdminV2JsonValue;
    }
  >({
    query: (body) => ({
      url: "/admin/v2/growth/sequences",
      method: "POST",
      data: body,
    }),
    invalidatesTags: [
      { type: "AdminV2GrowthSequences", id: "LIST" },
      { type: "AdminV2AuditLog", id: "LIST" },
    ],
  }),

  getAdminV2GrowthSequence: builder.query<
    AdminV2GrowthSequenceDetailResponse,
    { sequenceId: string }
  >({
    query: ({ sequenceId }) => ({
      url: `/admin/v2/growth/sequences/${sequenceId}`,
    }),
    providesTags: (_result, _error, { sequenceId }) => [
      { type: "AdminV2GrowthSequences", id: sequenceId },
    ],
    keepUnusedDataFor: 20,
  }),

  updateAdminV2GrowthSequence: builder.mutation<
    AdminV2GrowthSequenceDetailResponse,
    {
      sequenceId: string;
      body: {
        sequenceKey?: string;
        name?: string;
        description?: string | null;
        builderSchema?: AdminV2JsonValue;
        stopConditions?: AdminV2JsonValue;
        metadata?: AdminV2JsonValue;
      };
    }
  >({
    query: ({ sequenceId, body }) => ({
      url: `/admin/v2/growth/sequences/${sequenceId}`,
      method: "PUT",
      data: body,
    }),
    invalidatesTags: (_result, _error, { sequenceId }) => [
      { type: "AdminV2GrowthSequences", id: "LIST" },
      { type: "AdminV2GrowthSequences", id: sequenceId },
      { type: "AdminV2AuditLog", id: "LIST" },
    ],
  }),

  publishAdminV2GrowthSequence: builder.mutation<
    AdminV2GrowthSequenceDetailResponse,
    { sequenceId: string }
  >({
    query: ({ sequenceId }) => ({
      url: `/admin/v2/growth/sequences/${sequenceId}/publish`,
      method: "POST",
      data: {},
    }),
    invalidatesTags: (_result, _error, { sequenceId }) => [
      { type: "AdminV2GrowthSequences", id: "LIST" },
      { type: "AdminV2GrowthSequences", id: sequenceId },
      { type: "AdminV2AuditLog", id: "LIST" },
    ],
  }),

  enrollAdminV2GrowthSequencePerson: builder.mutation<
    AdminV2GrowthSequenceEnrollPersonResponse,
    {
      sequenceId: string;
      body: {
        personId: string;
        enrollmentSource?: string;
        idempotencyKey?: string;
        metadata?: AdminV2JsonValue;
      };
    }
  >({
    query: ({ sequenceId, body }) => ({
      url: `/admin/v2/growth/sequences/${sequenceId}/enrollments`,
      method: "POST",
      data: body,
    }),
    invalidatesTags: (_result, _error, { sequenceId, body }) => [
      { type: "AdminV2GrowthSequences", id: sequenceId },
      { type: "AdminV2GrowthSequences", id: "LIST" },
      {
        type: "AdminV2GrowthSequences",
        id: `PERSON_${body.personId}`,
      },
      { type: "AdminV2Jobs", id: "LIST" },
      { type: "AdminV2AuditLog", id: "LIST" },
    ],
  }),

  getAdminV2GrowthSequencePersonEnrollments: builder.query<
    AdminV2GrowthSequencePersonEnrollmentsResponse,
    {
      personId: string;
      organisationId: string;
      limit?: number;
      offset?: number;
    }
  >({
    query: ({ personId, ...params }) => ({
      url: `/admin/v2/growth/sequences/enrollments/people/${personId}`,
      params,
      // RecordWorkflowsSection explains an expected scoped denial inline.
      suppressAccessDeniedModal: true,
    }),
    providesTags: (_result, _error, { personId }) => [
      { type: "AdminV2GrowthSequences", id: `PERSON_${personId}` },
    ],
    keepUnusedDataFor: 20,
  }),

  unenrollAdminV2GrowthSequenceEnrollment: builder.mutation<
    AdminV2GrowthSequenceUnenrollResponse,
    {
      enrollmentId: string;
      body?: {
        reason?: string;
      };
    }
  >({
    query: ({ enrollmentId, body }) => ({
      url: `/admin/v2/growth/sequences/enrollments/${enrollmentId}/unenroll`,
      method: "POST",
      data: optionalMutationBody(body),
    }),
    invalidatesTags: (result, _error, { enrollmentId }) => [
      { type: "AdminV2GrowthSequences", id: "LIST" },
      { type: "AdminV2GrowthSequences", id: enrollmentId },
      ...(result?.enrollment.personId
        ? [
            {
              type: "AdminV2GrowthSequences" as const,
              id: `PERSON_${result.enrollment.personId}`,
            },
          ]
        : []),
      { type: "AdminV2AuditLog", id: "LIST" },
    ],
  }),
});
